"use client";

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import styles from './Table.module.css';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { TableProps, Column, SortDirection } from './Table.types';

// Default page size options
const DEFAULT_PAGE_SIZES = [10, 20, 50, 100];

const Table = <T extends Record<string, any>>({
  data = [],
  columns,
  keyField,
  className = '',
  emptyState = 'No data available',
  onRowClick,
  sortConfig,
  onSort,
  loading = false,
  loadingComponent = 'Loading...',
  pagination: paginationProp = false,
  expandable,
  bordered = true,
  hoverable = true,
  rowClassName,
}: TableProps<T>) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [pageSize, setPageSize] = useState<number>(
    typeof paginationProp === 'object' && paginationProp.pageSize 
      ? paginationProp.pageSize 
      : 10
  );
  const [currentPage, setCurrentPage] = useState<number>(
    typeof paginationProp === 'object' && paginationProp.currentPage 
      ? paginationProp.currentPage 
      : 1
  );

  const getKey = useCallback((item: T): string => {
    return typeof keyField === 'function' ? keyField(item) : String(item[keyField]);
  }, [keyField]);

  // Handle row expansion
  const toggleRow = useCallback((key: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }, []);

  // Handle sorting
  const handleSort = useCallback((key: string) => {
    if (!onSort) return;
    
    const direction: SortDirection = sortConfig?.key === key 
      ? sortConfig.direction === 'asc' 
        ? 'desc' 
        : sortConfig.direction === 'desc' 
          ? null 
          : 'asc'
      : 'asc';
    
    onSort(direction ? { key, direction } : null);
  }, [onSort, sortConfig]);

  // Apply pagination
  const paginatedData = useMemo(() => {
    if (!paginationProp) return data;

    // If we have totalItems from pagination prop and data length matches pageSize,
    // assume data is already paginated (like from API)
    const totalItems = typeof paginationProp === 'object' && paginationProp.totalItems
      ? paginationProp.totalItems
      : data.length;

    // If data length is less than or equal to pageSize, it's likely already paginated
    if (data.length <= pageSize && totalItems > pageSize) {
      return data; // Data is already for current page
    }

    // Otherwise, apply client-side pagination
    const startIndex = (currentPage - 1) * pageSize;
    return data.slice(startIndex, startIndex + pageSize);
  }, [data, currentPage, pageSize, paginationProp]);

  // Sync currentPage with pagination prop changes
  useEffect(() => {
    if (typeof paginationProp === 'object' && paginationProp.currentPage) {
      setCurrentPage(paginationProp.currentPage);
    }
  }, [paginationProp]);

  // Reset to first page if data changes
  useEffect(() => {
    if (paginationProp && currentPage > 1) {
      setCurrentPage(1);
    }
  }, [data.length, paginationProp]);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    if (typeof paginationProp === 'object' && paginationProp.onPageChange) {
      paginationProp.onPageChange(page);
    } else {
      setCurrentPage(page);
    }
    
    // Scroll to top of table
    const tableContainer = document.querySelector(`.${styles.tableContainer}`);
    if (tableContainer) {
      tableContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [paginationProp]);

  // Handle page size change
  const handlePageSizeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = parseInt(e.target.value, 10);
    setPageSize(newSize);
    setCurrentPage(1);
  }, []);

  // Calculate total pages
  const totalPages = useMemo(() => {
    if (!paginationProp) return 1;

    // Use totalItems from pagination prop if available, otherwise fall back to data.length
    const totalItems = typeof paginationProp === 'object' && paginationProp.totalItems
      ? paginationProp.totalItems
      : data.length;

    return Math.ceil(totalItems / pageSize);
  }, [data.length, pageSize, paginationProp]);

  // Get total items for display
  const totalItems = useMemo(() => {
    return typeof paginationProp === 'object' && paginationProp.totalItems
      ? paginationProp.totalItems
      : data.length;
  }, [data.length, paginationProp]);

  // Reset to first page if current page exceeds total pages
  React.useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      handlePageChange(1);
    }
  }, [currentPage, totalPages, handlePageChange]);

  if (loading) {
    return (
      <div className={`${styles.loadingState} ${className}`}>
        {typeof loadingComponent === 'string' ? (
          <div className={styles.loadingText}>{loadingComponent}</div>
        ) : (
          loadingComponent
        )}
      </div>
    );
  }

  if (data.length === 0) {
    return <div className={`${styles.emptyState} ${className}`}>{emptyState}</div>;
  }

  const renderHeaderCell = (column: Column<T>, index: number) => {
    const isSortable = column.sortable && onSort && sortConfig;
    const isSorted = sortConfig?.key === column.key;
    const sortIcon = isSorted 
      ? sortConfig.direction === 'asc' 
        ? '↑' 
        : '↓' 
      : '';
    
    // If this is the first column (name) and expandable, merge it with the expand column
    if (index === 0 && expandable && column.key === 'name') {
      return (
        <th 
          key={column.key} 
          className={`${styles.headerCell} 
            ${!column.showOnMobile ? styles.hideOnMobile : ''} 
            ${isSortable ? styles.sortableHeader : ''}
          `}
          onClick={() => isSortable && handleSort(column.key)}
          data-column={column.key}
          colSpan={2}
        >
          <div className={styles.headerCellContent}>
            {column.title}
            {isSortable && (
              <span className={`${styles.sortIcon} ${isSorted ? styles.active : ''}`}>
                {sortIcon}
              </span>
            )}
          </div>
        </th>
      );
    }
    
    return (
      <th 
        key={column.key} 
        className={`${styles.headerCell} 
          ${!column.showOnMobile ? styles.hideOnMobile : ''} 
          ${isSortable ? styles.sortableHeader : ''}
        `}
        style={{ width: column.width }}
        onClick={() => isSortable && handleSort(column.key)}
        data-column={column.key}
      >
        <div className={styles.headerCellContent}>
          {column.title}
          {isSortable && (
            <span className={`${styles.sortIcon} ${isSorted ? styles.active : ''}`}>
              {sortIcon}
            </span>
          )}
        </div>
      </th>
    );
  };

  const renderRow = (item: T, index: number) => {
    if (!item) return null;
    
    const key = getKey(item);
    const isExpanded = expandedRows.has(key);
    const rowClass = [
      (onRowClick || (expandable?.expandOnRowClick !== false && expandable)) ? styles.clickableRow : '',
      hoverable ? styles.hoverableRow : '',
      isExpanded ? styles.rowExpanded : '',
      typeof rowClassName === 'function' ? rowClassName(item, index) : rowClassName,
    ].filter(Boolean).join(' ');

    const handleRowClick = (e: React.MouseEvent) => {
      if (expandable) {
        toggleRow(key);
      }
      onRowClick?.(item);
    };
    
    // Skip rendering if item is invalid
    if (!item) return null;

    return (
      <React.Fragment key={key}>
        <tr 
          className={rowClass}
          onClick={handleRowClick}
        >
          {expandable && (
            <td className={styles.expandCell}>
              <span className={`${styles.expandIcon} ${isExpanded ? styles.expanded : ''}`}>
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </span>
            </td>
          )}
          {columns.map((column) => (
            <td 
              key={`${key}-${column.key}`}
              className={`${!column.showOnMobile ? styles.hideOnMobile : ''}`}
              data-column={column.key}
              data-label={column.title}
            >
              {column.render ? column.render(item) : String(item[column.key] ?? '')}
            </td>
          ))}
        </tr>
        {expandable && isExpanded && (
          <tr className={`${styles.expandedRow} ${expandable.expandedClassName || ''}`}>
            <td colSpan={columns.length + (expandable ? 1 : 0)}>
              <div className={styles.expandedContent}>
                {expandable.expandedRowRender(item)}
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  const renderPagination = () => {
    if (!paginationProp) return null;

    const pageSizeOptions = 
      (typeof paginationProp === 'object' && paginationProp.pageSizeOptions) || 
      DEFAULT_PAGE_SIZES;
    
    const showPageSizeSelector = 
      typeof paginationProp === 'object' && 
      (paginationProp.showPageSizeSelector ?? true);
    
    return (
      <div className={styles.paginationContainer}>
        {showPageSizeSelector && (
          <div className={styles.pageSizeSelector}>
            <span>Show:</span>
            <select 
              value={pageSize}
              onChange={handlePageSizeChange}
              className={styles.pageSizeSelect}
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span>entries</span>
          </div>
        )}
        
        <div className={styles.pagination}>
          <button
            className={`${styles.pageButton} ${currentPage === 1 ? styles.disabled : ''}`}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
          >
            «
          </button>
          <button
            className={`${styles.pageButton} ${currentPage === 1 ? styles.disabled : ''}`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            ‹
          </button>
          
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage > totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            
            return (
              <button
                key={pageNum}
                className={`${styles.pageButton} ${currentPage === pageNum ? styles.active : ''}`}
                onClick={() => handlePageChange(pageNum)}
              >
                {pageNum}
              </button>
            );
          })}
          
          <button
            className={`${styles.pageButton} ${currentPage === totalPages ? styles.disabled : ''}`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            ›
          </button>
          <button
            className={`${styles.pageButton} ${currentPage === totalPages ? styles.disabled : ''}`}
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
          >
            »
          </button>
        </div>
        
        <div className={styles.pageInfo}>
          Showing {Math.min((currentPage - 1) * pageSize + 1, totalItems)} to{' '}
          {Math.min(currentPage * pageSize, totalItems)} of {totalItems} entries
        </div>
      </div>
    );
  };

  return (
    <div className={`${styles.tableContainer} ${bordered ? styles.bordered : ''} ${className}`}>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          {expandable && (
            <colgroup>
              <col style={{ width: '32px' }} />
              {columns.map((column) => (
                <col key={column.key} style={{ width: column.width || 'auto' }} />
              ))}
            </colgroup>
          )}
          {!expandable && (
            <colgroup>
              {columns.map((column) => (
                <col key={column.key} style={{ width: column.width || 'auto' }} />
              ))}
            </colgroup>
          )}
          <thead>
            <tr>
              {columns.map((column, index) => renderHeaderCell(column, index))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((item, index) => renderRow(item, index))}
          </tbody>
        </table>
      </div>
      {paginationProp && renderPagination()}
    </div>
  );
};

export default React.memo(Table);
